import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface ProjectCase {
  id: string;
  title: string;
  description: string;
  image: string;
  category: string;
  grade: string;
  difficulty: 'easy' | 'medium' | 'hard';
  duration: string;
  tools: string[];
  skills: string[];
  highlights: string[];
  studentCount: number;
  rating: number;
  module: string;
  moduleRoute: string;
}

const categories = [
  { id: 'all', label: '全部案例', icon: 'ri-apps-line' },
  { id: 'ai-app', label: 'AI应用', icon: 'ri-robot-2-line' },
  { id: 'iot', label: '物联网', icon: 'ri-wifi-line' },
  { id: 'data', label: '数据分析', icon: 'ri-bar-chart-grouped-line' },
  { id: 'creative', label: '创意设计', icon: 'ri-palette-line' },
  { id: 'cross', label: '跨学科', icon: 'ri-links-line' },
];

const projectCases: ProjectCase[] = [
  {
    id: 'smart-campus',
    title: '智慧校园环境监测系统',
    description: '利用物联网传感器采集校园温湿度、空气质量、噪音等数据，通过AI算法分析环境变化趋势，自动调节教室空调和通风设备，为师生创造舒适的学习环境。',
    image: 'https://public.readdy.ai/ai/img_res/77dc4c745851d2f4ef990c0d58747d1b.jpg',
    category: 'iot',
    grade: '7年级',
    difficulty: 'medium',
    duration: '3周',
    tools: ['物联网模拟器', '数据可视化', 'Python基础'],
    skills: ['传感器应用', '数据采集', '自动化控制', '数据分析'],
    highlights: ['实时数据监控面板', '智能预警系统', '自动化联动控制', '数据报告生成'],
    studentCount: 1286,
    rating: 4.8,
    module: '物联网联动模拟器',
    moduleRoute: '/middle/iot-simulator',
  },
  {
    id: 'ai-tutor',
    title: 'AI学科辅导智能体',
    description: '使用低代码平台搭建一个能够辅导数学、英语等学科的AI智能体。配置专业知识库，设定教学风格，让AI能够根据学生的薄弱环节进行针对性辅导和练习推荐。',
    image: 'https://readdy.ai/api/search-image?query=AI%20tutoring%20chatbot%20interface%20with%20math%20equations%20and%20English%20vocabulary%20on%20screen%20friendly%20robot%20assistant%20helping%20student%20learn%20clean%20modern%20educational%20technology%20illustration%20warm%20colors&width=600&height=400&seq=mc2&orientation=landscape',
    category: 'ai-app',
    grade: '7-9年级',
    difficulty: 'medium',
    duration: '2周',
    tools: ['智能体搭建工坊', '提示词工程', 'RAG知识库'],
    skills: ['智能体配置', '提示词设计', '知识库构建', '对话测试'],
    highlights: ['个性化辅导方案', '错题智能分析', '学习进度追踪', '多学科覆盖'],
    studentCount: 2150,
    rating: 4.9,
    module: '智能体搭建工坊',
    moduleRoute: '/middle/agent-builder',
  },
  {
    id: 'waste-sorting',
    title: 'AI垃圾分类识别助手',
    description: '训练一个图像分类模型，能够识别常见垃圾的类别（可回收物、有害垃圾、厨余垃圾、其他垃圾）。结合摄像头实时识别，帮助社区居民正确进行垃圾分类。',
    image: 'https://readdy.ai/api/search-image?query=AI%20waste%20sorting%20classification%20system%20with%20camera%20scanning%20garbage%20items%20showing%20recyclable%20hazardous%20kitchen%20waste%20categories%20on%20digital%20screen%20clean%20green%20eco%20friendly%20technology%20illustration&width=600&height=400&seq=mc3&orientation=landscape',
    category: 'ai-app',
    grade: '8年级',
    difficulty: 'hard',
    duration: '4周',
    tools: ['机器学习游乐场', '图像识别', '数据标注'],
    skills: ['数据收集', '模型训练', '图像分类', '模型评估'],
    highlights: ['自建训练数据集', '模型准确率优化', '实时识别演示', '社区推广方案'],
    studentCount: 980,
    rating: 4.7,
    module: '机器学习游乐场',
    moduleRoute: '/middle/ml-playground',
  },
  {
    id: 'weather-predict',
    title: '校园气象站与天气预测',
    description: '搭建校园气象数据采集系统，收集温度、湿度、气压、风速等数据。运用机器学习算法分析历史气象数据，建立简单的天气预测模型，为校园活动提供天气参考。',
    image: 'https://readdy.ai/api/search-image?query=School%20weather%20station%20with%20meteorological%20instruments%20and%20data%20charts%20showing%20temperature%20humidity%20pressure%20wind%20speed%20predictions%20on%20digital%20dashboard%20clean%20scientific%20illustration%20style&width=600&height=400&seq=mc4&orientation=landscape',
    category: 'data',
    grade: '8年级',
    difficulty: 'hard',
    duration: '4周',
    tools: ['物联网模拟器', '机器学习游乐场', '数据可视化'],
    skills: ['数据采集', '统计分析', '回归预测', '可视化展示'],
    highlights: ['多维度数据采集', '趋势分析图表', '预测模型构建', '准确率对比实验'],
    studentCount: 756,
    rating: 4.6,
    module: '机器学习游乐场',
    moduleRoute: '/middle/ml-playground',
  },
  {
    id: 'story-generator',
    title: 'AI创意故事生成器',
    description: '通过提示词工程技术，设计一套完整的AI故事创作系统。学生可以设定故事主题、角色、场景等要素，AI根据提示词生成富有创意的故事内容，并配上AI生成的插图。',
    image: 'https://readdy.ai/api/search-image?query=AI%20creative%20story%20generator%20interface%20with%20colorful%20storybook%20illustrations%20fairy%20tale%20characters%20and%20text%20generation%20on%20screen%20whimsical%20artistic%20digital%20illustration%20warm%20fantasy%20colors&width=600&height=400&seq=mc5&orientation=landscape',
    category: 'creative',
    grade: '7年级',
    difficulty: 'easy',
    duration: '2周',
    tools: ['提示词工程实验室', '智能体搭建工坊'],
    skills: ['提示词设计', '创意写作', '图文搭配', '作品展示'],
    highlights: ['多风格故事模板', '角色性格设定', 'AI配图生成', '电子绘本导出'],
    studentCount: 1820,
    rating: 4.9,
    module: '提示词工程实验室',
    moduleRoute: '/middle/prompt-lab',
  },
  {
    id: 'smart-agriculture',
    title: '智慧农业灌溉系统',
    description: '结合生物学和信息技术知识，设计一套智能灌溉方案。通过土壤湿度传感器监测植物需水状况，AI算法根据天气预报和植物生长周期自动制定灌溉计划，实现精准节水。',
    image: 'https://readdy.ai/api/search-image?query=Smart%20agriculture%20irrigation%20system%20with%20soil%20moisture%20sensors%20automated%20watering%20plants%20in%20greenhouse%20digital%20control%20panel%20showing%20water%20schedule%20clean%20green%20technology%20illustration&width=600&height=400&seq=mc6&orientation=landscape',
    category: 'cross',
    grade: '7年级',
    difficulty: 'medium',
    duration: '3周',
    tools: ['物联网模拟器', '可视化编程', '数据分析'],
    skills: ['跨学科整合', '传感器应用', '算法设计', '节能环保'],
    highlights: ['土壤数据实时监测', '智能灌溉策略', '节水效果对比', '生长数据记录'],
    studentCount: 1050,
    rating: 4.7,
    module: '跨学科项目空间',
    moduleRoute: '/middle/interdisciplinary-project',
  },
  {
    id: 'neural-network-art',
    title: '神经网络艺术风格迁移',
    description: '深入理解卷积神经网络原理，体验AI艺术风格迁移技术。将普通照片转换为梵高、莫奈等大师的绘画风格，探索神经网络在艺术创作中的应用，感受科技与艺术的融合。',
    image: 'https://readdy.ai/api/search-image?query=Neural%20network%20art%20style%20transfer%20showing%20original%20photo%20transforming%20into%20Van%20Gogh%20Monet%20painting%20style%20with%20neural%20network%20visualization%20in%20background%20artistic%20colorful%20digital%20illustration&width=600&height=400&seq=mc7&orientation=landscape',
    category: 'creative',
    grade: '9年级',
    difficulty: 'hard',
    duration: '3周',
    tools: ['神经网络实验室', '图像处理', 'Python基础'],
    skills: ['神经网络原理', '图像处理', '模型应用', '艺术鉴赏'],
    highlights: ['CNN原理可视化', '多种艺术风格', '实时风格转换', '作品展览分享'],
    studentCount: 680,
    rating: 4.8,
    module: '神经网络实验室',
    moduleRoute: '/middle/neural-network',
  },
  {
    id: 'traffic-analysis',
    title: '校园交通流量分析系统',
    description: '利用数据采集和机器学习技术，分析校园周边不同时段的交通流量。通过数据可视化展示高峰时段分布，为学校制定错峰放学方案提供数据支持，提升校园周边交通安全。',
    image: 'https://readdy.ai/api/search-image?query=School%20traffic%20flow%20analysis%20system%20with%20heat%20map%20showing%20pedestrian%20vehicle%20density%20around%20campus%20data%20visualization%20charts%20graphs%20clean%20modern%20urban%20planning%20illustration&width=600&height=400&seq=mc8&orientation=landscape',
    category: 'data',
    grade: '8-9年级',
    difficulty: 'medium',
    duration: '3周',
    tools: ['机器学习游乐场', '数据可视化', '统计分析'],
    skills: ['数据采集', '统计分析', '可视化展示', '方案设计'],
    highlights: ['多时段数据采集', '热力图可视化', '高峰预测模型', '安全改善建议'],
    studentCount: 520,
    rating: 4.5,
    module: '机器学习游乐场',
    moduleRoute: '/middle/ml-playground',
  },
  {
    id: 'chatbot-customer',
    title: '校园服务智能客服',
    description: '为学校图书馆、食堂、社团等部门搭建AI智能客服。学生通过配置知识库和对话流程，让AI能够回答常见问题、提供服务指引，提升校园信息化服务水平。',
    image: 'https://readdy.ai/api/search-image?query=School%20AI%20customer%20service%20chatbot%20interface%20with%20library%20cafeteria%20club%20information%20friendly%20chat%20bubbles%20on%20tablet%20screen%20clean%20modern%20educational%20service%20illustration&width=600&height=400&seq=mc9&orientation=landscape',
    category: 'ai-app',
    grade: '7-8年级',
    difficulty: 'easy',
    duration: '2周',
    tools: ['智能体搭建工坊', '提示词工程', '知识库管理'],
    skills: ['需求分析', '知识库构建', '对话设计', '用户测试'],
    highlights: ['多场景覆盖', '自然语言理解', '常见问题自动回复', '满意度评价系统'],
    studentCount: 1580,
    rating: 4.8,
    module: '智能体搭建工坊',
    moduleRoute: '/middle/agent-builder',
  },
];

export default function ProjectCases() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedProject, setExpandedProject] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const filteredProjects = activeCategory === 'all'
    ? projectCases
    : projectCases.filter(p => p.category === activeCategory);

  const displayedProjects = showAll ? filteredProjects : filteredProjects.slice(0, 6);

  const getDifficultyInfo = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return { label: '入门', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' };
      case 'medium': return { label: '进阶', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' };
      case 'hard': return { label: '挑战', color: 'bg-rose-100 text-rose-700', dot: 'bg-rose-500' };
      default: return { label: '入门', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' };
    }
  };

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalf = rating - fullStars >= 0.5;
    for (let i = 0; i < fullStars; i++) {
      stars.push(<i key={`full-${i}`} className="ri-star-fill text-amber-400"></i>);
    }
    if (hasHalf) {
      stars.push(<i key="half" className="ri-star-half-fill text-amber-400"></i>);
    }
    for (let i = stars.length; i < 5; i++) {
      stars.push(<i key={`empty-${i}`} className="ri-star-line text-gray-300"></i>);
    }
    return stars;
  };

  return (
    <section className="py-20 px-6 bg-gradient-to-b from-white via-blue-50/30 to-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-teal-100 text-teal-700 px-5 py-2 rounded-full mb-5">
            <i className="ri-rocket-2-line text-lg"></i>
            <span className="font-semibold text-sm">动手实践 · 学以致用</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">实践项目案例</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            精选真实场景项目，从简单到复杂，在实践中掌握AI核心技能
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            { icon: 'ri-folder-open-line', value: `${projectCases.length}+`, label: '精选项目', color: 'from-teal-500 to-emerald-500' },
            { icon: 'ri-user-heart-line', value: '10,800+', label: '参与学生', color: 'from-blue-500 to-cyan-500' },
            { icon: 'ri-award-line', value: '4.7', label: '平均评分', color: 'from-amber-500 to-orange-500' },
            { icon: 'ri-book-mark-line', value: '6', label: '覆盖模块', color: 'from-rose-500 to-pink-500' },
          ].map((stat, idx) => (
            <div key={idx} className="bg-white rounded-2xl shadow-lg p-5 text-center hover:shadow-xl transition-all">
              <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center mx-auto mb-3`}>
                <i className={`${stat.icon} text-2xl text-white`}></i>
              </div>
              <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
              <div className="text-sm text-gray-500">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => { setActiveCategory(cat.id); setShowAll(false); setExpandedProject(null); }}
              className={`px-5 py-2.5 rounded-full font-semibold text-sm transition-all cursor-pointer whitespace-nowrap flex items-center space-x-2 ${
                activeCategory === cat.id
                  ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-lg shadow-teal-200'
                  : 'bg-white text-gray-600 hover:bg-gray-100 shadow-md'
              }`}
            >
              <i className={cat.icon}></i>
              <span>{cat.label}</span>
            </button>
          ))}
        </div>

        {/* Project Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedProjects.map((project) => {
            const diffInfo = getDifficultyInfo(project.difficulty);
            const isExpanded = expandedProject === project.id;

            return (
              <div
                key={project.id}
                className={`bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all overflow-hidden group ${isExpanded ? 'lg:col-span-3 md:col-span-2' : ''}`}
              >
                {isExpanded ? (
                  /* Expanded View */
                  <div className="grid md:grid-cols-2 gap-0">
                    <div className="relative h-72 md:h-auto overflow-hidden">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover object-top"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${diffInfo.color}`}>
                          {diffInfo.label}
                        </span>
                        <span className="px-3 py-1 bg-white/90 text-gray-700 rounded-full text-xs font-semibold">
                          {project.grade}
                        </span>
                      </div>
                    </div>
                    <div className="p-8">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 mb-2">{project.title}</h3>
                          <div className="flex items-center space-x-3 text-sm text-gray-500">
                            <span className="flex items-center space-x-1">
                              <i className="ri-time-line"></i>
                              <span>{project.duration}</span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <i className="ri-user-line"></i>
                              <span>{project.studentCount.toLocaleString()}人参与</span>
                            </span>
                            <span className="flex items-center space-x-1">
                              {renderStars(project.rating)}
                              <span className="ml-1 font-semibold text-gray-700">{project.rating}</span>
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => setExpandedProject(null)}
                          className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-all cursor-pointer flex-shrink-0"
                        >
                          <i className="ri-close-line text-gray-600"></i>
                        </button>
                      </div>

                      <p className="text-gray-600 mb-5 leading-relaxed">{project.description}</p>

                      <div className="mb-5">
                        <h4 className="text-sm font-bold text-gray-800 mb-2">项目亮点</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {project.highlights.map((h, i) => (
                            <div key={i} className="flex items-center space-x-2 text-sm text-gray-700">
                              <i className="ri-checkbox-circle-fill text-teal-500"></i>
                              <span>{h}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="mb-5">
                        <h4 className="text-sm font-bold text-gray-800 mb-2">使用工具</h4>
                        <div className="flex flex-wrap gap-2">
                          {project.tools.map((tool, i) => (
                            <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold">
                              {tool}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="mb-6">
                        <h4 className="text-sm font-bold text-gray-800 mb-2">培养技能</h4>
                        <div className="flex flex-wrap gap-2">
                          {project.skills.map((skill, i) => (
                            <span key={i} className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-xs font-semibold">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <span className="text-sm text-gray-500">
                          <i className="ri-apps-line mr-1"></i>
                          所属模块：{project.module}
                        </span>
                        <button 
                          onClick={() => navigate(project.moduleRoute)}
                          className="px-5 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all cursor-pointer whitespace-nowrap text-sm"
                        >
                          <i className="ri-play-circle-line mr-1"></i>
                          开始实践
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Collapsed Card View */
                  <>
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                      <div className="absolute top-3 left-3 flex items-center space-x-2">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${diffInfo.color}`}>
                          {diffInfo.label}
                        </span>
                      </div>
                      <div className="absolute top-3 right-3">
                        <span className="px-2.5 py-1 bg-white/90 text-gray-700 rounded-full text-xs font-semibold">
                          {project.grade}
                        </span>
                      </div>
                      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                        <span className="text-white text-xs flex items-center space-x-1">
                          <i className="ri-time-line"></i>
                          <span>{project.duration}</span>
                        </span>
                        <span className="text-white text-xs flex items-center space-x-1">
                          <i className="ri-user-line"></i>
                          <span>{project.studentCount.toLocaleString()}人</span>
                        </span>
                      </div>
                    </div>

                    <div className="p-5">
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                        {project.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed">
                        {project.description}
                      </p>

                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {project.skills.slice(0, 3).map((skill, i) => (
                          <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                            {skill}
                          </span>
                        ))}
                        {project.skills.length > 3 && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">
                            +{project.skills.length - 3}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <div className="flex items-center space-x-1 text-sm">
                          {renderStars(project.rating)}
                          <span className="ml-1 font-semibold text-gray-700">{project.rating}</span>
                        </div>
                        <button
                          onClick={() => setExpandedProject(project.id)}
                          className="text-sm text-teal-600 font-semibold hover:text-teal-700 transition-colors cursor-pointer whitespace-nowrap flex items-center space-x-1"
                        >
                          <span>查看详情</span>
                          <i className="ri-arrow-right-s-line"></i>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Show More Button */}
        {filteredProjects.length > 6 && !showAll && (
          <div className="text-center mt-10">
            <button
              onClick={() => setShowAll(true)}
              className="px-8 py-3 bg-white text-gray-700 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all cursor-pointer whitespace-nowrap border border-gray-200 hover:border-teal-300"
            >
              <i className="ri-arrow-down-line mr-2"></i>
              查看更多案例（共{filteredProjects.length}个）
            </button>
          </div>
        )}

        {showAll && filteredProjects.length > 6 && (
          <div className="text-center mt-10">
            <button
              onClick={() => { setShowAll(false); setExpandedProject(null); }}
              className="px-8 py-3 bg-white text-gray-700 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all cursor-pointer whitespace-nowrap border border-gray-200 hover:border-teal-300"
            >
              <i className="ri-arrow-up-line mr-2"></i>
              收起
            </button>
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-16 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-3xl p-10 text-center text-white shadow-xl">
          <div className="max-w-2xl mx-auto">
            <i className="ri-lightbulb-flash-line text-5xl mb-4 opacity-90"></i>
            <h3 className="text-3xl font-bold mb-4">有好的项目创意？</h3>
            <p className="text-lg opacity-90 mb-8">
              欢迎同学们提交自己的项目创意，优秀作品将被收录到案例库中，与全区同学分享！
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button className="px-8 py-3 bg-white text-teal-600 rounded-lg font-bold hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer">
                <i className="ri-add-circle-line mr-2"></i>
                提交项目创意
              </button>
              <button className="px-8 py-3 bg-teal-600 text-white rounded-lg font-bold hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer">
                <i className="ri-trophy-line mr-2"></i>
                查看优秀作品
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
